/**
 * Treatment status & scheduling — single source of truth.
 *
 * Historically the completion/due logic was re-implemented inline in
 * Treatments.js, the snapshot card and the grids, with diverging semantics
 * (strict `===` vs. numeric comparison) and a dependency on a `TreatmentDate`
 * field that is never written by the web app. Every consumer must go through
 * this module instead.
 *
 * Scheduling model (mirrors calculateNextDoseDate in chatToolsService and the
 * mobile app's PhoneHistory convention):
 * - The anchor for the next application is the most recent application
 *   (last PhoneHistory entry / LastTreatmentDate). Without any application the
 *   treatment start (StartDateSeconds, falling back to `t`) is the anchor.
 * - Next due = anchor when nothing was administered yet, otherwise
 *   anchor + TreatmentInterval days.
 * - A persisted `TreatmentDate` (potentially written by backend jobs) is
 *   respected as an override so this module stays forward-compatible.
 */

export type TreatmentStatus = "completed" | "overdue" | "dueToday" | "upcoming";

export interface TreatmentLike {
	Done?: boolean;
	TreatmentAmount?: number | string | null;
	TreatmentCount?: number | string | null;
	TreatmentInterval?: number | string | null;
	TreatmentDate?: unknown;
	LastTreatmentDate?: unknown;
	StartDateSeconds?: number | string | null;
	StartDate?: string | null;
	PhoneHistory?: Array<{ t?: number; timestamp?: number }> | null;
	t?: number | string | null;
	[key: string]: unknown;
}

export interface TreatmentSchedule {
	completed: boolean;
	/** Date of the next due application; null when the treatment is completed. */
	nextDueDate: Date | null;
	/** Date of the most recent recorded application, if any. */
	lastApplicationDate: Date | null;
	/** Start of the treatment (StartDateSeconds → StartDate → `t`), if derivable. */
	startDate: Date | null;
	/** Planned number of applications (>= 1 when a plan exists). */
	totalApplications: number;
	/** Applications recorded so far (clamped to >= 0). */
	doneApplications: number;
	remainingApplications: number;
	intervalDays: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Epoch numbers reach us in both units: `StartDateSeconds` really is in
 * seconds in documents written by the mobile app, while the web app writes
 * milliseconds into the same field. Same heuristic as
 * utils/suspiciousAnimals.js — seconds timestamps are ~1e9, milliseconds ~1e12+.
 */
const normalizeEpochNumberToMs = (value: number): number =>
	value < 1e12 ? value * 1000 : value;

/**
 * Converts the many date shapes found in treatment documents (Date, epoch
 * number in seconds or milliseconds, ISO string, Firestore Timestamp) into a
 * Date, or null when unparseable.
 */
export function toDateSafe(input: unknown): Date | null {
	if (input === null || input === undefined || input === "") return null;
	if (input instanceof Date) {
		return Number.isNaN(input.getTime()) ? null : input;
	}
	if (typeof input === "number") {
		return Number.isFinite(input) ? new Date(normalizeEpochNumberToMs(input)) : null;
	}
	if (typeof input === "string") {
		const numeric = Number(input);
		if (input.trim() !== "" && Number.isFinite(numeric) && /^\d+$/.test(input.trim())) {
			return new Date(normalizeEpochNumberToMs(numeric));
		}
		const parsed = new Date(input);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}
	if (typeof input === "object") {
		const shape = input as { seconds?: unknown; _seconds?: unknown };
		if (typeof shape.seconds === "number") return new Date(shape.seconds * 1000);
		if (typeof shape._seconds === "number") return new Date(shape._seconds * 1000);
	}
	return null;
}

const toFiniteNumber = (value: unknown): number | null => {
	if (value === null || value === undefined || value === "") return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

/**
 * A treatment counts as completed when it was explicitly closed (`Done`) or
 * when at least the planned number of applications was recorded. Comparison is
 * numeric on purpose: legacy documents mix number and string types.
 */
export function isTreatmentCompleted(treatment: TreatmentLike | null | undefined): boolean {
	if (!treatment) return false;
	if (treatment.Done === true) return true;

	const amount = toFiniteNumber(treatment.TreatmentAmount);
	const count = toFiniteNumber(treatment.TreatmentCount);
	if (amount === null || count === null) return false;
	if (amount < 1) return false;

	return count >= amount;
}

/**
 * Resolves the treatment start in the same field precedence every consumer used
 * ad hoc before: the explicit start fields first, the document timestamp last.
 */
const getStartDate = (treatment: TreatmentLike): Date | null =>
	toDateSafe(treatment.StartDateSeconds) ||
	toDateSafe(treatment.StartDate) ||
	toDateSafe(treatment.t);

const getLastApplicationDate = (treatment: TreatmentLike): Date | null => {
	const history = Array.isArray(treatment.PhoneHistory) ? treatment.PhoneHistory : [];
	const lastEntry = history.length > 0 ? history[history.length - 1] : null;
	return (
		toDateSafe(lastEntry?.t ?? lastEntry?.timestamp) ||
		toDateSafe(treatment.LastTreatmentDate)
	);
};

/**
 * Derives the full schedule (progress + next due date) for a treatment.
 */
export function getTreatmentSchedule(treatment: TreatmentLike | null | undefined): TreatmentSchedule {
	const empty: TreatmentSchedule = {
		completed: false,
		nextDueDate: null,
		lastApplicationDate: null,
		startDate: null,
		totalApplications: 0,
		doneApplications: 0,
		remainingApplications: 0,
		intervalDays: 1,
	};
	if (!treatment) return empty;

	const totalApplications = Math.max(toFiniteNumber(treatment.TreatmentAmount) ?? 1, 1);
	const doneApplications = Math.max(toFiniteNumber(treatment.TreatmentCount) ?? 0, 0);
	const intervalDays = Math.max(toFiniteNumber(treatment.TreatmentInterval) ?? 1, 1);
	const completed = isTreatmentCompleted(treatment);
	const lastApplicationDate = getLastApplicationDate(treatment);
	const startDate = getStartDate(treatment);

	let nextDueDate: Date | null = null;
	if (!completed) {
		const override = toDateSafe(treatment.TreatmentDate);
		if (override) {
			nextDueDate = override;
		} else if (lastApplicationDate) {
			nextDueDate = new Date(lastApplicationDate.getTime() + intervalDays * MS_PER_DAY);
		} else if (startDate) {
			nextDueDate =
				doneApplications > 0
					? new Date(startDate.getTime() + doneApplications * intervalDays * MS_PER_DAY)
					: startDate;
		}
	}

	return {
		completed,
		nextDueDate,
		lastApplicationDate,
		startDate,
		totalApplications,
		doneApplications,
		remainingApplications: Math.max(totalApplications - doneApplications, 0),
		intervalDays,
	};
}

const startOfDay = (date: Date): number => {
	const copy = new Date(date.getTime());
	copy.setHours(0, 0, 0, 0);
	return copy.getTime();
};

/**
 * Classifies a treatment relative to `now` (defaults to the current time).
 * Treatments without any derivable date fall back to "dueToday" so they stay
 * visible in the actionable list instead of silently disappearing.
 */
export function getTreatmentStatus(
	treatment: TreatmentLike | null | undefined,
	now: Date = new Date()
): TreatmentStatus {
	const schedule = getTreatmentSchedule(treatment);
	if (schedule.completed) return "completed";
	if (!schedule.nextDueDate) return "dueToday";

	const todayStart = startOfDay(now);
	const dueStart = startOfDay(schedule.nextDueDate);
	if (dueStart < todayStart) return "overdue";
	if (dueStart === todayStart) return "dueToday";
	return "upcoming";
}

/** Convenience: overdue + dueToday — everything a farmer should act on now. */
export function isTreatmentActionable(
	treatment: TreatmentLike | null | undefined,
	now: Date = new Date()
): boolean {
	const status = getTreatmentStatus(treatment, now);
	return status === "overdue" || status === "dueToday";
}

export interface TreatmentStatusCounts {
	/** Non-archived treatments considered. */
	total: number;
	completed: number;
	overdue: number;
	dueToday: number;
	upcoming: number;
	/**
	 * Everything that is not finished yet: `total - completed`, i.e.
	 * `overdue + dueToday + upcoming`. This is the "open treatments" a farmer
	 * still has on the books — NOT the subset that needs action today. For the
	 * actionable subset use `overdue + dueToday` (see `isTreatmentActionable`).
	 */
	open: number;
}

/**
 * THE single source for every treatment count surface.
 *
 * Dashboard action queue, treatment statistics card, Treatments page chips and
 * the animal grids all used to roll their own tally — with strict `===`
 * comparisons that broke on string/number mixes ("3" !== 3), with over-delivery
 * (7 of 3 doses) counting as open forever, and with due dates anchored on the
 * treatment START instead of the schedule's next due date. That is why the
 * dashboard could claim "5 overdue" while /treatments showed none. Every count
 * must be derived from this function so the surfaces can no longer disagree.
 *
 * The buckets `completed`, `overdue`, `dueToday` and `upcoming` are mutually
 * exclusive and sum up to `total`; `open` is the derived `total - completed`.
 * Archived documents are skipped defensively — they are tombstones, not work.
 *
 * @param treatments Treatment documents (null/undefined tolerated).
 * @param now Reference time; injectable so tests and memos stay deterministic.
 */
export function countTreatmentsByStatus(
	treatments: TreatmentLike[] | null | undefined,
	now: Date = new Date()
): TreatmentStatusCounts {
	const counts: TreatmentStatusCounts = {
		total: 0,
		completed: 0,
		overdue: 0,
		dueToday: 0,
		upcoming: 0,
		open: 0,
	};

	(Array.isArray(treatments) ? treatments : []).forEach((treatment) => {
		if (!treatment || treatment.Archived) return;

		counts.total += 1;
		const status = getTreatmentStatus(treatment, now);
		counts[status] += 1;
	});

	counts.open = counts.total - counts.completed;
	return counts;
}
